<template>
    <h4 class="section-title">{{ translate("federation.federation_members") }}</h4>

    <div class="buttons">
        <button class="button is-primary" @click="openInviteModal">
            <span class="icon">
                <i class="fas fa-user-plus"></i>
            </span>
            <span>{{ translate("federation.add_member") }}</span>
        </button>
    </div>

    <template v-if="initDone">
        <div class="box is-condensed" v-for="federationMember in store.federationMembers" :key="federationMember.id">
            <div class="columns is-vcentered">
                <div class="column is-4">
                    <p class="has-text-weight-bold clarity-mask">{{ federationMember.firstName }} {{ federationMember.lastName }}</p>
                    <p class="clarity-mask">{{ federationMember.email }}</p>
                </div>

                <template v-if="editingId === federationMember.id">
                    <div class="column">
                        <FederationRoleSelect v-model="editRole" />
                        <StaffBundlePicker v-if="editRole === FederationRoleEnum.Staff" v-model="editBundles" />
                    </div>
                    <div class="column is-narrow">
                        <div class="buttons is-flex-wrap-nowrap">
                            <LoadingButton
                                :is-loading="savingInProgress === federationMember.id"
                                :disabled="!canSaveEdit"
                                class="button is-small is-success"
                                @click="saveEdit(federationMember)"
                            >
                                <span class="icon">
                                    <i class="fas fa-check"></i>
                                </span>
                                <span>{{ translate("common.save") }}</span>
                            </LoadingButton>
                            <button class="button is-small is-danger" @click="cancelEdit">
                                {{ translate("common.cancel") }}
                            </button>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="column">
                        <p class="has-text-weight-semibold">
                            {{ federationMember.role ? translateFederationRole(federationMember.role) : "---" }}
                        </p>
                        <div
                            v-if="federationMember.role === FederationRoleEnum.Staff"
                            class="is-flex is-flex-wrap-wrap mt-2"
                            style="gap: 0.375rem"
                        >
                            <span
                                v-for="bundle in bundlesToOptions(federationMember.bundles || [])"
                                :key="bundle.key"
                                class="tag is-info is-light"
                            >
                                {{ bundle.label }}
                            </span>
                        </div>
                    </div>
                    <div class="column is-narrow">
                        <div class="buttons is-flex-wrap-nowrap">
                            <button
                                class="button is-small"
                                @click="startEdit(federationMember)"
                                :disabled="federationMember.id === store.userId"
                            >
                                <span class="icon">
                                    <i class="fas fa-pen"></i>
                                </span>
                                <span>{{ translate("common.edit") }}</span>
                            </button>
                            <button
                                class="button is-small is-danger"
                                @click="removeMember(federationMember.id)"
                                :disabled="federationMember.id === store.userId"
                            >
                                <span class="icon">
                                    <i class="fas fa-trash-alt"></i>
                                </span>
                                <span>{{ translate("common.delete") }}</span>
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </template>
    <div class="columns is-centered is-mobile" v-else>
        <LoadingSpinner2 class="has-added-margin" />
    </div>

    <InviteMemberModal ref="inviteMemberModal" />
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from "vue";
    import federationsApi, {
        FederationMemberData,
        FederationRole,
        FederationRoleEnum,
        translateFederationRole,
    } from "../../Api/federationsApi";
    import { useFederationDashboardStore } from "./federationManagementStore";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import InviteMemberModal from "./InviteMemberModal.vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import FederationRoleSelect from "./FederationRoleSelect.vue";
    import StaffBundlePicker from "../../Common/StaffBundlePicker.vue";
    import { translate } from "../../Common/translations";
    import { Bundle, bundlesToOptions } from "../../Common/consts";

    const store = useFederationDashboardStore();
    const initDone = ref(false);

    function refreshMembers() {
        return store.refreshFederationMembers().catch(handleError);
    }

    const editingId = ref(0);
    const editRole = ref<FederationRole | null>(null);
    const editBundles = ref<Bundle[]>([]);
    const savingInProgress = ref(0);

    const canSaveEdit = computed(() => editRole.value !== FederationRoleEnum.Staff || editBundles.value.length > 0);

    function startEdit(federationMemberData: FederationMemberData) {
        editingId.value = federationMemberData.id;
        editRole.value = federationMemberData.role;
        editBundles.value = federationMemberData.bundles || [];
    }

    function cancelEdit() {
        editingId.value = 0;
    }

    function saveEdit(federationMemberData: FederationMemberData) {
        savingInProgress.value = federationMemberData.id;

        return federationsApi
            .updateFederationMember({
                federationId: store.federationId,
                userId: federationMemberData.id,
                role: editRole.value as FederationRole,
                bundles: editBundles.value,
            })
            .then(() => {
                successToast(translate("federation.role_updated"));
                editingId.value = 0;
                return refreshMembers();
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = 0;
            });
    }

    const inviteMemberModal = ref<ComponentExposed<typeof InviteMemberModal> | null>(null);

    function openInviteModal() {
        inviteMemberModal.value?.open();
    }

    onMounted(() => {
        initDone.value = false;
        return refreshMembers().finally(() => {
            initDone.value = true;
        });
    });

    function removeMember(userId: number) {
        if (!confirm(translate("common.are_you_sure"))) {
            return Promise.resolve();
        }

        return federationsApi
            .removeFederationMember(store.federationId, userId)
            .then(() => {
                successToast(translate("federation.member_removed"));
                return refreshMembers();
            })
            .catch(handleError);
    }
</script>

<style scoped></style>
